import React, { FC, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Alert, Button, Spin, Table, PaginationProps } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import Main from 'components/layout/Main';
import { ChevronDownIcon, SearchIcon } from 'components/Elements/CustomSVGIcon';
import TextInput from 'components/Elements/TextInput';
import StatusBadge from 'components/Elements/StatusBadge';
import SortableHeader from 'components/Elements/SortableHeader';
import useColumnSort from 'core/utils/useColumnSort';
import { CombinedStates, AppDispatch } from 'core/types';
import { ADMIN_ACCESS_LEVELS } from 'core/utils/value';
import { fetchFilteredIncidents, exportIncidents, createIncident, updateIncident, markCompleteIncident, reopenIncident, createDiaryEntry, updateDiaryEntry, previewDiaryEntryEmail, previewIncidentUpdateEmail } from 'core/incidents/incidentsApi';
import { Incident, IncidentFilterPayload, IncidentExportPayload, DEFAULT_PAGE_SIZE, DiaryEntry, IncidentEmailPreview } from 'core/incidents/types';
import { fetchSiteDetail } from 'core/sites/sitesApi';
import { clearSiteDetail } from 'core/sites/sitesSlice';
import { clearIncidents } from 'core/incidents/incidentsSlice';
import { buildMainToolbar } from 'core/utils/mainToolbar';
import IncidentExpandedRow from './components/IncidentExpandedRow';
import IncidentFilterModal, { IncidentFilterValues, IncidentFilterOptions } from './components/IncidentFilterModal';
import EditIncidentModal, { EditIncidentValues, EditIncidentOptions, resolveEffectiveConfig } from './components/EditIncidentModal';
import MarkCompleteModal from './components/MarkCompleteModal';
import ReopenIncidentModal from './components/ReopenIncidentModal';
import AddDiaryEntryModal, { DiaryEntryFormValues } from './components/AddDiaryEntryModal';
import buildDiaryFormData, { diaryAttachmentNames } from './diaryEntryFormData';
import { buildIncidentCreatePayload, buildIncidentUpdatePayload } from './incidentPayloads';
import { isRetailSiteId } from './retailSite';
import ExportDropdown from './components/ExportDropdown';
import SiteIncidentHeader from './components/SiteIncidentHeader';
import './incidents.scss';

// Terminal statuses an incident cannot be reopened into. Name-based to match the
// rest of the app (backend IncidentStatus::STATUS_CLOSED / STATUS_CANCELLED);
// the reopen endpoint also hard-rejects reopening into Closed as a safety net.
const TERMINAL_INCIDENT_STATUS_LABELS = ['Closed', 'Cancelled'];

const Incidents: FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const navigate = useNavigate();
    const { id: siteEncryptedId } = useParams<{ id?: string }>();
    const isSiteMode = !!siteEncryptedId;

    // Site info: prefer route state (from SiteInfo), fall back to fetched site detail
    const routeState = location.state as { siteName?: string; siteId?: string; openReport?: boolean } | null;
    const { siteDetail, siteDetailError, isFetchingSiteDetail } = useSelector((state: CombinedStates) => state.sites);
    const siteName = isSiteMode ? (routeState?.siteName || siteDetail?.name || '') : '';
    const siteNumericId = isSiteMode ? (routeState?.siteId || (siteDetail ? String(siteDetail.id) : '')) : '';

    const { userData } = useSelector((state: CombinedStates) => state.user);
    const {
        incidents, isFetching, pagination, filterData, summaryData, isExporting,
    } = useSelector((state: CombinedStates) => state.incidents);

    const [searchText, setSearchText] = useState<string>('');
    const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
    const { sortField, sortDirection, onColumnSort } = useColumnSort('', 'desc');
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingIncident, setEditingIncident] = useState<Incident | null>(null);
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [isMarkCompleteModalOpen, setIsMarkCompleteModalOpen] = useState(false);
    const [markCompleteIncidentRecord, setMarkCompleteIncidentRecord] = useState<Incident | null>(null);
    // Edits made in the same session as the status change to Closed. Held while
    // the Mark as Complete modal collects the root cause, then saved with it in
    // one request — otherwise check-in/out times and every other change typed
    // alongside the status would be thrown away.
    const [pendingCloseValues, setPendingCloseValues] = useState<EditIncidentValues | null>(null);
    const [isReopenModalOpen, setIsReopenModalOpen] = useState(false);
    const [reopenIncidentRecord, setReopenIncidentRecord] = useState<Incident | null>(null);
    const [isDiaryModalOpen, setIsDiaryModalOpen] = useState(false);
    const [diaryIncident, setDiaryIncident] = useState<Incident | null>(null);
    const [editingDiaryEntry, setEditingDiaryEntry] = useState<DiaryEntry | null>(null);
    const diaryIncidentRef = useRef<Incident | null>(null);
    const [filterValues, setFilterValues] = useState<IncidentFilterValues>({
        site: [],
        category: [],
        remedyReference: [],
        status: [],
        priority: [],
        critical: [],
        dateLoggedStart: '',
        dateLoggedEnd: '',
    });

    const [activeCard, setActiveCard] = useState<string | null>(null);

    const getStatusId = useMemo(() => {
        const map = new Map<string, string>();
        filterData.statuses?.forEach((s) => {
            map.set(s.name.toLowerCase(), String(s.id));
        });
        return (name: string) => map.get(name);
    }, [filterData.statuses]);

    const last24h = useMemo(() => dayjs().subtract(24, 'hour').format('YYYY-MM-DD HH:mm:ss'), []);

    const statCards = useMemo(() => {
        const closedId = getStatusId('closed');
        const cancelledId = getStatusId('cancelled');
        const completedIds = [closedId, cancelledId].filter(Boolean) as string[];
        const activeIds = (filterData.statuses ?? [])
            .map((s) => String(s.id))
            .filter((id) => !completedIds.includes(id));

        return [
            {
                id: 1, number: summaryData?.new ?? 0, title: 'New', statusFilter: [] as string[], criticalFilter: [] as string[], dateLoggedStart: last24h, dateLoggedEnd: '',
            },
            {
                id: 2, number: summaryData?.active ?? 0, title: 'Active', statusFilter: activeIds, criticalFilter: [] as string[], dateLoggedStart: '', dateLoggedEnd: '',
            },
            {
                id: 3, number: summaryData?.critical ?? 0, title: 'Critical', statusFilter: [] as string[], criticalFilter: ['Yes'], dateLoggedStart: '', dateLoggedEnd: '',
            },
            {
                id: 4, number: summaryData?.completed ?? 0, title: 'Completed', statusFilter: completedIds, criticalFilter: [] as string[], dateLoggedStart: '', dateLoggedEnd: '',
            },
        ];
    }, [summaryData, getStatusId, filterData.statuses, last24h]);

    const handleCardClick = (card: typeof statCards[number]) => {
        if (activeCard === card.title) {
            setActiveCard(null);
            setFilterValues((prev) => ({
                ...prev, status: [], critical: [], dateLoggedStart: '', dateLoggedEnd: '',
            }));
            return;
        }
        setActiveCard(card.title);
        setFilterValues((prev) => ({
            ...prev,
            status: card.statusFilter,
            critical: card.criticalFilter,
            dateLoggedStart: card.dateLoggedStart,
            dateLoggedEnd: card.dateLoggedEnd,
        }));
    };

    // Close all modals on route change
    useEffect(() => {
        setIsEditModalOpen(false);
        setIsReportModalOpen(false);
        setIsFilterModalOpen(false);
        setIsMarkCompleteModalOpen(false);
        setIsDiaryModalOpen(false);
        setEditingIncident(null);
        setPendingCloseValues(null);
    }, [location.pathname]);

    // In site mode without route state, fetch site detail for name/id
    useEffect(() => {
        if (isSiteMode && !routeState?.siteId && siteEncryptedId) {
            dispatch(fetchSiteDetail(siteEncryptedId));
        }
        return () => {
            if (isSiteMode) dispatch(clearSiteDetail());
        };
    }, [isSiteMode, siteEncryptedId, routeState?.siteId, dispatch]);

    // Admin-only capability gate. Incident creation and diary create/edit/preview
    // are all admin-only on the backend (IncidentStoreRequest and
    // IncidentDiaryEntry{Store,Update,Preview}Request::authorize()), so a single
    // flag gates all of them. Security officers can view but not mutate.
    const isAdmin = ADMIN_ACCESS_LEVELS.includes(userData?.role?.accessLevel);

    // Opened from the Sites 3-dots "Report Incident" action — jump straight into
    // the report form for this site.
    useEffect(() => {
        if (isAdmin && isSiteMode && routeState?.openReport) {
            setIsReportModalOpen(true);
        }
    }, [isAdmin, isSiteMode, routeState?.openReport]);

    const buildPayload = useCallback((page = 1): IncidentFilterPayload => {
        const payload: IncidentFilterPayload = { page, pageSize: DEFAULT_PAGE_SIZE };
        if (searchText) payload.q = searchText;
        if (isSiteMode) {
            payload.siteId = siteNumericId;
        } else if (filterValues.site.length > 0) {
            payload.site = filterValues.site;
        }
        if (filterValues.category.length > 0) payload.problemCategory = filterValues.category;
        if (filterValues.remedyReference.length > 0) payload.remedy = filterValues.remedyReference;
        if (filterValues.status.length > 0) payload.status = filterValues.status;
        if (filterValues.priority.length > 0) payload.priority = filterValues.priority;
        if (filterValues.critical.length === 1) {
            const [criticalValue] = filterValues.critical;
            if (criticalValue) payload.is_critical = criticalValue === 'Yes' ? '1' : '0';
        }
        if (filterValues.dateLoggedStart) payload.dateLoggedFrom = filterValues.dateLoggedStart;
        if (filterValues.dateLoggedEnd) payload.dateLoggedTo = filterValues.dateLoggedEnd;
        if (sortField) {
            payload.sort = sortDirection;
            payload.sortField = sortField;
        }
        return payload;
    }, [searchText, filterValues, sortDirection, sortField, isSiteMode, siteNumericId]);

    const handleFetchIncidents = useCallback((page = 1) => {
        if (isSiteMode && !siteNumericId) return;
        dispatch(fetchFilteredIncidents(buildPayload(page)));
    }, [dispatch, buildPayload, isSiteMode, siteNumericId]);

    // Keep refs to latest values so the debounced wrapper always reads current state
    const buildPayloadRef = useRef(buildPayload);
    buildPayloadRef.current = buildPayload;
    const isSiteModeRef = useRef(isSiteMode);
    isSiteModeRef.current = isSiteMode;
    const siteNumericIdRef = useRef(siteNumericId);
    siteNumericIdRef.current = siteNumericId;

    // Stable debounced function created once, calls through refs to avoid stale closures
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedFetchIncidents = useMemo(
        () => debounce(() => {
            if (isSiteModeRef.current && !siteNumericIdRef.current) return;
            dispatch(fetchFilteredIncidents(buildPayloadRef.current(1)));
        }, 1000),
        [dispatch],
    );

    // Initial load – only on mount and when siteNumericId resolves
    const initialLoadDone = useRef(false);

    // Reset state when switching between site mode and global mode
    const prevIsSiteMode = useRef(isSiteMode);
    useEffect(() => {
        if (prevIsSiteMode.current !== isSiteMode) {
            prevIsSiteMode.current = isSiteMode;
            dispatch(clearIncidents());
            initialLoadDone.current = false;
            setSearchText('');
            setActiveCard(null);
            setFilterValues({
                site: [],
                category: [],
                remedyReference: [],
                status: [],
                priority: [],
                critical: [],
                dateLoggedStart: '',
                dateLoggedEnd: '',
            });
        }
    }, [isSiteMode, dispatch]);
    useEffect(() => {
        if (isSiteMode && !siteNumericId) return;
        if (initialLoadDone.current) return;
        initialLoadDone.current = true;
        dispatch(fetchFilteredIncidents(buildPayloadRef.current(1)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isSiteMode, siteNumericId]);

    // Debounced fetch when search, filters, or sort changes
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return undefined;
        }
        debouncedFetchIncidents();
        return () => debouncedFetchIncidents.cancel();
    }, [searchText, filterValues, sortField, sortDirection, debouncedFetchIncidents]);

    const filterOptions: IncidentFilterOptions = useMemo(() => ({
        sites: filterData.sites?.map((s) => ({ label: s.name, value: String(s.id) })) || [],
        categories: filterData.problemCategories?.map((c) => ({ label: c.name, value: String(c.id) })) || [],
        remedyReferences: filterData.remedyReferences?.map((r) => ({ label: r.reference, value: r.reference })) || [],
        statuses: filterData.statuses?.map((s) => ({ label: s.name, value: String(s.id) })) || [],
        priorities: filterData.priorities?.map((p) => ({ label: p.name, value: String(p.id) })) || [],
        criticalLevels: [{ label: 'Yes', value: 'Yes' }, { label: 'No', value: 'No' }],
    }), [filterData]);

    const editOptions: EditIncidentOptions = useMemo(() => {
        const categoryFieldConfig: EditIncidentOptions['categoryFieldConfig'] = {};
        filterData.problemCategories?.forEach((c) => {
            categoryFieldConfig[String(c.id)] = {
                showRemedyReference: c.showRemedyReference ?? false,
                showKhcReference: c.showKhcReference ?? false,
                showMaximoReference: c.showMaximoReference ?? false,
                showConceptReference: c.showConceptReference ?? false,
                showPoliceReference: c.showPoliceReference ?? false,
                requireCheckInOut: c.requireCheckInOut ?? false,
                defaultPriorityId: c.defaultPriorityId ?? null,
                visibleRetail: c.visibleRetail ?? true,
                isCriticalCategory: c.isCriticalCategory ?? false,
                priorityIds: c.priorityIds ?? [],
            };
        });
        const siteTypes: Record<string, string> = {};
        const siteGuardingTypes: Record<string, string> = {};
        filterData.sites?.forEach((s) => {
            if (s.type) siteTypes[String(s.id)] = s.type;
            if (s.guardingType) siteGuardingTypes[String(s.id)] = s.guardingType;
        });

        return {
            sites: filterData.sites?.map((s) => ({ label: s.name, value: String(s.id) })) || [],
            problemCategories: filterData.problemCategories?.map((c) => ({ label: c.name, value: String(c.id) })) || [],
            statuses: filterData.statuses?.map((s) => ({ label: s.name, value: String(s.id) })) || [],
            priorities: filterData.priorities?.map((p) => ({ label: p.name, value: String(p.id) })) || [],
            categoryFieldConfig,
            siteTypes,
            siteGuardingTypes,
        };
    }, [filterData]);

    // Statuses an incident can be reopened into: everything except the terminal
    // Closed/Cancelled states.
    const reopenStatuses = useMemo(
        () => editOptions.statuses.filter((s) => !TERMINAL_INCIDENT_STATUS_LABELS.includes(s.label)),
        [editOptions.statuses],
    );

    // Status value that closes (completes) an incident.
    const closedStatusValue = useMemo(
        () => editOptions.statuses.find((s) => s.label === 'Closed')?.value || '',
        [editOptions.statuses],
    );

    const handleApplyFilters = (values: IncidentFilterValues) => {
        setActiveCard(null);
        setFilterValues(values);
    };

    const handleEditIncident = (record: Incident) => {
        setEditingIncident(record);
        setIsEditModalOpen(true);
    };

    const isRetailSite = (siteValue: string) => isRetailSiteId(editOptions, siteValue);

    // On a retail store's own incidents page the Critical flag doesn't exist:
    // no column, no filter (the modal drops the checkbox for the same reason).
    const isRetailSiteMode = isSiteMode && isRetailSite(siteNumericId);

    const handleSaveIncident = async (values: EditIncidentValues): Promise<boolean> => {
        if (!editingIncident) return false;

        // Moving the status to Closed completes the incident, which is done via
        // the Mark as Complete modal (it collects the Root Cause + Resolution
        // Details). Close the edit modal and hand off to that modal.
        const isClosing = !editingIncident.completedAt && !!closedStatusValue && values.status === closedStatusValue;
        if (isClosing) {
            setPendingCloseValues(values);
            setMarkCompleteIncidentRecord(editingIncident);
            setIsMarkCompleteModalOpen(true);
            return true;
        }

        const cfg = resolveEffectiveConfig(editOptions.categoryFieldConfig[values.problemCategory]);
        const retail = isRetailSite(values.site);
        const showRemedy = cfg.showRemedyReference && !retail;
        const payload = buildIncidentUpdatePayload(values, cfg, showRemedy, retail);
        const result = await dispatch(updateIncident({ id: editingIncident.id, payload }));
        if (result.meta.requestStatus === 'fulfilled') {
            handleFetchIncidents();
            return true;
        }
        return false;
    };

    // Renders the distribution email the pending edit would send. Guarded the
    // same way the diary preview is: admin-only, and only once "send report to
    // distribution centre" is ticked — a preview exposes the recipient list and
    // the rendered email, so it carries the same restrictions as the send.
    const handleEditIncidentPreview = async (values: EditIncidentValues): Promise<IncidentEmailPreview | null> => {
        if (!editingIncident || !values.sendToDistribution || !isAdmin) return null;

        const cfg = resolveEffectiveConfig(editOptions.categoryFieldConfig[values.problemCategory]);
        const retail = isRetailSite(values.site);
        const showRemedy = cfg.showRemedyReference && !retail;
        // Built from the same mapper the save uses, so the preview can only ever
        // show the email the save would send.
        const payload = buildIncidentUpdatePayload(values, cfg, showRemedy, retail);
        const result = await dispatch(previewIncidentUpdateEmail({ id: editingIncident.id, payload }));

        return previewIncidentUpdateEmail.fulfilled.match(result) ? result.payload : null;
    };

    const handleReportIncident = async (values: EditIncidentValues): Promise<boolean> => {
        const cfg = resolveEffectiveConfig(editOptions.categoryFieldConfig[values.problemCategory]);
        const retail = isRetailSite(values.site);
        const showRemedy = cfg.showRemedyReference && !retail;
        const payload = buildIncidentCreatePayload(values, cfg, showRemedy, retail);
        const result = await dispatch(createIncident(payload));
        if (result.meta.requestStatus === 'fulfilled') {
            handleFetchIncidents();
            return true;
        }
        return false;
    };

    const handleMarkComplete = async (rootCause: string, resolutionDetails: string): Promise<boolean> => {
        if (!markCompleteIncidentRecord) return false;

        // Reached from the edit modal: save the edit and the completion together,
        // so the check-in/out times entered alongside the status change survive.
        // The update payload carries the root cause for exactly this case.
        if (pendingCloseValues) {
            const values = pendingCloseValues;
            const cfg = resolveEffectiveConfig(editOptions.categoryFieldConfig[values.problemCategory]);
            const retail = isRetailSite(values.site);
            const showRemedy = cfg.showRemedyReference && !retail;
            const payload = buildIncidentUpdatePayload(values, cfg, showRemedy, retail, rootCause, resolutionDetails);
            const updateResult = await dispatch(updateIncident({ id: markCompleteIncidentRecord.id, payload }));
            if (updateResult.meta.requestStatus !== 'fulfilled') return false;
            handleFetchIncidents();
            setMarkCompleteIncidentRecord(null);
            setPendingCloseValues(null);
            return true;
        }

        // Reached straight from the row action: nothing else to save.
        const result = await dispatch(markCompleteIncident({
            id: markCompleteIncidentRecord.id,
            payload: { root_cause: rootCause, resolution_details: resolutionDetails },
        }));
        if (result.type.endsWith('/rejected')) return false;
        handleFetchIncidents();
        setMarkCompleteIncidentRecord(null);
        return true;
    };

    const handleReopen = async (incidentStatusId: string): Promise<boolean> => {
        if (!reopenIncidentRecord) return false;
        const result = await dispatch(reopenIncident({
            id: reopenIncidentRecord.id,
            payload: { incident_status_id: Number(incidentStatusId) },
        }));
        if (result.type.endsWith('/rejected')) return false;
        handleFetchIncidents();
        setReopenIncidentRecord(null);
        return true;
    };

    const handleAddDiaryEntry = (record: Incident) => {
        setEditingDiaryEntry(null);
        setDiaryIncident(record);
        diaryIncidentRef.current = record;
        setIsDiaryModalOpen(true);
    };

    const handleEditDiaryEntry = (record: Incident, entry: DiaryEntry) => {
        setEditingDiaryEntry(entry);
        setDiaryIncident(record);
        diaryIncidentRef.current = record;
        setIsDiaryModalOpen(true);
    };

    const closeDiaryModal = () => {
        setIsDiaryModalOpen(false);
        setDiaryIncident(null);
        setEditingDiaryEntry(null);
        diaryIncidentRef.current = null;
    };

    const handleDiaryEntrySubmit = async (values: DiaryEntryFormValues): Promise<void> => {
        if (!diaryIncident) return;

        const submittingIncidentId = diaryIncident.id;

        const result = editingDiaryEntry
            ? await dispatch(updateDiaryEntry({
                incidentId: submittingIncidentId,
                diaryEntryId: editingDiaryEntry.id,
                text: values.text,
                entryOccurredAt: values.entryOccurredAt,
            }))
            : await dispatch(createDiaryEntry({ incidentId: submittingIncidentId, formData: buildDiaryFormData(values) }));

        if (result.meta.requestStatus === 'fulfilled') {
            handleFetchIncidents();
            // Only close if the modal is still showing the same incident
            if (diaryIncidentRef.current?.id === submittingIncidentId) {
                closeDiaryModal();
            }
        }
    };

    const handleDiaryEntryPreview = async (values: DiaryEntryFormValues): Promise<IncidentEmailPreview | null> => {
        // Defence in depth at the invocation boundary: only ever request a
        // preview for a user with the diary-management capability who has
        // actually ticked "send to distribution". The Preview button already
        // enforces both, and the backend independently authorises admin-only —
        // but guard here too so a future caller/refactor can't reveal the
        // recipient list or email content outside those rules.
        if (!diaryIncident || !values.sentToDistribution || !isAdmin) return null;

        // Derive names from the same source the submit path sends, so the preview
        // lists exactly the attachments a real submit would include.
        const attachmentNames = diaryAttachmentNames(values);
        const result = await dispatch(previewDiaryEntryEmail({
            incidentId: diaryIncident.id,
            text: values.text,
            entryOccurredAt: values.entryOccurredAt,
            attachmentNames,
        }));

        return previewDiaryEntryEmail.fulfilled.match(result) ? result.payload : null;
    };

    const handleClearAllFilters = () => {
        setSearchText('');
        setActiveCard(null);
        setFilterValues({
            site: [],
            category: [],
            remedyReference: [],
            status: [],
            priority: [],
            critical: [],
            dateLoggedStart: '',
            dateLoggedEnd: '',
        });
    };

    const getActiveFilterCount = () => {
        let count = 0;
        if (filterValues.site.length) count++;
        if (filterValues.category.length) count++;
        if (filterValues.remedyReference.length) count++;
        if (filterValues.status.length) count++;
        if (filterValues.priority.length) count++;
        if (filterValues.critical.length) count++;
        if (filterValues.dateLoggedStart) count++;
        if (filterValues.dateLoggedEnd) count++;
        return count;
    };

    const handlePageChange = (page: number) => {
        handleFetchIncidents(page);
        window.scrollTo({ top: 0, behavior: 'auto' });
    };

    const buildExportPayload = (): Omit<IncidentExportPayload, 'exportType'> => {
        const payload: Omit<IncidentExportPayload, 'exportType'> = {};
        if (searchText) payload.q = searchText;
        if (isSiteMode) {
            payload.site = [String(siteNumericId)];
        } else if (filterValues.site?.length > 0) {
            payload.site = filterValues.site;
        }
        if (filterValues.category?.length > 0) payload.problemCategory = filterValues.category;
        if (filterValues.remedyReference?.length > 0) payload.remedy = filterValues.remedyReference;
        if (filterValues.status?.length > 0) payload.status = filterValues.status;
        if (filterValues.priority?.length > 0) payload.priority = filterValues.priority;
        if (filterValues.critical?.length === 1) {
            const [criticalValue] = filterValues.critical;
            if (criticalValue) payload.is_critical = criticalValue === 'Yes' ? '1' : '0';
        }
        if (filterValues.dateLoggedStart) payload.dateLoggedFrom = filterValues.dateLoggedStart;
        if (filterValues.dateLoggedEnd) payload.dateLoggedTo = filterValues.dateLoggedEnd;
        if (sortField) {
            payload.sort = sortDirection;
            payload.sortField = sortField;
        }
        return payload;
    };

    const handleExport = () => {
        if (isSiteMode && !siteNumericId) return;
        dispatch(exportIncidents({ ...buildExportPayload(), exportType: 'csv' }));
    };

    // Site-scoped incidents (/site/:id/incidents) belong under Sites, so that is
    // the item highlighted rather than Incidents.
    const toolbar = buildMainToolbar(userData?.role?.accessLevel, isSiteMode ? { activeLink: '/sites' } : {});

    const [collapsingRowKey, setCollapsingRowKey] = useState<number | null>(null);
    const collapsingRowIdRef = useRef<number | null>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (collapsingRowKey === null) return undefined;
        const row = tableContainerRef.current?.querySelector('.row-collapsing') ?? null;
        if (!row) {
            setExpandedRowKeys((keys) => (keys[0] === collapsingRowKey ? [] : keys));
            setCollapsingRowKey(null);
            collapsingRowIdRef.current = null;
            return undefined;
        }
        const onEnd = () => {
            const id = collapsingRowIdRef.current;
            setExpandedRowKeys((keys) => (keys[0] === id ? [] : keys));
            setCollapsingRowKey(null);
            collapsingRowIdRef.current = null;
        };
        row.addEventListener('animationend', onEnd, { once: true });
        return () => row.removeEventListener('animationend', onEnd);
    }, [collapsingRowKey]);

    const onExpand = (expanded: boolean, record: Incident) => {
        if (expanded) {
            collapsingRowIdRef.current = null;
            setCollapsingRowKey(null);
            setExpandedRowKeys([record.id]);
        } else {
            collapsingRowIdRef.current = record.id;
            setCollapsingRowKey(record.id);
        }
    };

    const expandedRowRender = (record: object) => (
        <IncidentExpandedRow
            record={record as Incident}
            onEditClick={() => handleEditIncident(record as Incident)}
            onMarkCompleteClick={() => {
                setPendingCloseValues(null);
                setMarkCompleteIncidentRecord(record as Incident);
                setIsMarkCompleteModalOpen(true);
            }}
            onReopenClick={() => {
                setReopenIncidentRecord(record as Incident);
                setIsReopenModalOpen(true);
            }}
            onViewSiteDetails={() => {
                const encryptedId = (record as Incident).site?.encryptedId;
                if (encryptedId) navigate(`/site/${encryptedId}`);
            }}
            onAddDiaryEntry={() => handleAddDiaryEntry(record as Incident)}
            onEditDiaryEntry={(entry) => handleEditDiaryEntry(record as Incident, entry)}
            canManageDiaryEntries={isAdmin} />
    );

    const columns = [
        {
            title: <SortableHeader title="Incident Reference" field="id" currentSortField={sortField} currentSortDirection={sortDirection} onSort={onColumnSort} />,
            dataIndex: 'id',
            key: 'id',
            width: 140,
            // Mirror the requests table's "on behalf of" pattern: the primary
            // reference on the first line, the Remedy Ref as a muted sub-line.
            render: (_: unknown, record: Incident) => (
                <div>
                    <div>{record.id}</div>
                    {record.remedyReference && (
                        <div className="on-behalf-label">Remedy Ref: {record.remedyReference}</div>
                    )}
                </div>
            ),
        },
        {
            title: <SortableHeader title="Site" field="site" currentSortField={sortField} currentSortDirection={sortDirection} onSort={onColumnSort} />,
            key: 'site',
            // Show "<site type>: <site name>" (e.g. "DC: Some Site"), falling
            // back to just the name when the type isn't set.
            render: (_: unknown, record: Incident) => {
                const { type, name } = record.site || {};
                return type ? `${type}: ${name}` : (name || '-');
            },
        },
        {
            title: <SortableHeader title="Problem Category" field="problemCategory" currentSortField={sortField} currentSortDirection={sortDirection} onSort={onColumnSort} />,
            dataIndex: ['incidentProblemCategory', 'name'],
            key: 'problemCategory',
        },
        {
            title: <SortableHeader title="Priority" field="priority" currentSortField={sortField} currentSortDirection={sortDirection} onSort={onColumnSort} />,
            dataIndex: ['incidentPriority', 'name'],
            key: 'priority',
            // Retail incidents have no priority — show a dash rather than a blank cell.
            render: (name: string | null) => name || '-',
        },
        {
            title: <SortableHeader title="Critical" field="is_critical" currentSortField={sortField} currentSortDirection={sortDirection} onSort={onColumnSort} />,
            dataIndex: 'isCritical',
            key: 'critical',
            render: (isCritical: boolean) => (isCritical ? 'Yes' : 'No'),
        },
        {
            title: <SortableHeader title="Status" field="status" currentSortField={sortField} currentSortDirection={sortDirection} onSort={onColumnSort} />,
            dataIndex: ['incidentStatus', 'name'],
            key: 'status',
            render: (status: string) => <StatusBadge status={status} />,
        },
        {
            title: <SortableHeader title="Date Logged" field="date_logged" currentSortField={sortField} currentSortDirection={sortDirection} onSort={onColumnSort} />,
            dataIndex: 'dateLogged',
            key: 'dateLogged',
            render: (dateLogged: string) => {
                if (!dateLogged) return '-';
                const d = new Date(dateLogged);
                return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
            },
        },
        {
            title: '',
            key: 'expand',
            width: 50,
            render: (_: unknown, record: Incident) => {
                const isExpanded = expandedRowKeys.includes(record.id);
                return (
                    <span
                        role="button"
                        tabIndex={0}
                        aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                        aria-expanded={isExpanded}
                        className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
                        onClick={(e) => { e.stopPropagation(); onExpand(!isExpanded, record); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onExpand(!isExpanded, record); } }}>
                        <ChevronDownIcon w={18} h={18} />
                    </span>
                );
            },
        },
    ];

    const hiddenColumnKeys = [
        ...(isSiteMode ? ['site'] : []),
        // Retail stores have no critical incidents, so the column would only
        // ever read "No".
        ...(isRetailSiteMode ? ['critical'] : []),
    ];
    const displayColumns = hiddenColumnKeys.length
        ? columns.filter((c) => !hiddenColumnKeys.includes(String(c.key)))
        : columns;

    const lastPage = Math.ceil(pagination.total / pagination.pageSize);

    const handleTotal = (_total: number, range: [number, number]): JSX.Element => (
        <div style={{ left: 0, position: 'absolute' }}>
            Result: <span className="accent-text">{range[0].toLocaleString()} - {range[1].toLocaleString()} of {pagination.total.toLocaleString()}</span>
        </div>
    );

    const itemRender: PaginationProps['itemRender'] = (page, type, originalElement) => {
        if (type === 'page') {
            return page === pagination.currentPage ? <div className="request-table-pagination">Page <span className="accent-text">{page}</span> of {lastPage}</div> : null;
        }
        return originalElement;
    };

    return (
        <Main
            className="incidents"
            title={<div>Incidents</div>}
            hideSubHeader
            subHeaderTitle="Incidents"
            subHeaderHasBackLink
            subHeaderBackLink="/"
            menuWidth={350}
            menuPlacement="right"
            toolbar={toolbar}
            showFooter={false}
            floatingHeader={false}
            footerContent=""
            floatingFooter={false}
            breadcrumb={[]}>
            <div className="main-container incidents-page">
                {isSiteMode ? (
                    <SiteIncidentHeader
                        siteName={siteName}
                        {...(isAdmin ? { onReportIncident: () => setIsReportModalOpen(true) } : {})} />
                ) : (
                    <>
                        <div className="request-card-content" data-tour="incidents-summary">
                            {statCards.map((card) => (
                                <div
                                    key={card.id}
                                    className={`request-card clickable${activeCard === card.title ? ' active' : ''}`}
                                    onClick={() => handleCardClick(card)}
                                    style={{ cursor: 'pointer' }}>
                                    <div className="content">
                                        <span className="request-number">{card.number}</span>
                                        <span className="request-text">{card.title}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="incidents-title-row">
                            <h2>Incidents</h2>
                            {isAdmin && (
                                <Button type="primary" className="report-button" data-tour="incidents-report" onClick={() => setIsReportModalOpen(true)}>Report New Incident</Button>
                            )}
                        </div>
                    </>
                )}

                {isSiteMode && !siteNumericId && siteDetailError && (
                    <Alert
                        type="error"
                        showIcon
                        message="Unable to load site details. Please go back and try again." />
                )}

                {isSiteMode && !siteNumericId && isFetchingSiteDetail && (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Spin size="large" />
                    </div>
                )}

                <div className="incidents-search-container">
                    <div className="filter-container">
                        <TextInput
                            leftIcon={<SearchIcon />}
                            placeholder="Search"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)} />
                        <span
                            className="filters-button"
                            onClick={() => setIsFilterModalOpen(true)}>
                            Filters{getActiveFilterCount() > 0 ? ` (${getActiveFilterCount()} Applied)` : ''}
                        </span>
                        <span
                            className="clear-all-link"
                            onClick={handleClearAllFilters}>
                            Clear all
                        </span>
                    </div>
                    <ExportDropdown onExport={handleExport} disabled={isExporting} />
                </div>

                <div className="incidents-table-container" ref={tableContainerRef}>
                    <Table
                        loading={isFetching}
                        dataSource={incidents}
                        columns={displayColumns}
                        rowKey="id"
                        className="incidents-table"
                        onRow={(record: Incident) => ({
                            onClick: () => {
                                const isExpanded = expandedRowKeys.includes(record.id);
                                onExpand(!isExpanded, record);
                            },
                            style: { cursor: 'pointer' },
                        })}
                        rowClassName={(record: Incident) => (
                            expandedRowKeys.includes(record.id) ? 'row-expanded' : ''
                        )}
                        pagination={{
                            current: pagination.currentPage,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            showSizeChanger: false,
                            itemRender,
                            showTotal: handleTotal,
                            onChange: handlePageChange,
                        }}
                        expandable={{
                            expandedRowRender,
                            expandedRowClassName: (record: Incident) => (
                                collapsingRowKey === record.id ? 'row-collapsing' : ''
                            ),
                            expandedRowKeys,
                            onExpand: onExpand as (expanded: boolean, record: object) => void,
                            expandIcon: () => null,
                            columnWidth: 0,
                        }} />
                </div>
            </div>
            <IncidentFilterModal
                visible={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                onApply={handleApplyFilters}
                onClearAll={handleClearAllFilters}
                filterOptions={filterOptions}
                initialValues={filterValues}
                hideSiteFilter={isSiteMode}
                hideCriticalFilter={isRetailSiteMode} />
            <EditIncidentModal
                visible={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onSave={handleSaveIncident}
                incident={editingIncident}
                editOptions={editOptions}
                isSiteMode={isSiteMode}
                siteId={siteNumericId}
                siteName={siteName}
                onPreview={handleEditIncidentPreview} />
            <EditIncidentModal
                visible={isReportModalOpen}
                onClose={() => setIsReportModalOpen(false)}
                onSave={handleReportIncident}
                incident={null}
                editOptions={editOptions}
                isSiteMode={isSiteMode}
                siteId={siteNumericId}
                siteName={siteName} />
            <MarkCompleteModal
                visible={isMarkCompleteModalOpen}
                onClose={() => {
                    setIsMarkCompleteModalOpen(false);
                    setMarkCompleteIncidentRecord(null);
                    setPendingCloseValues(null);
                }}
                onConfirm={handleMarkComplete}
                isCritical={Boolean(pendingCloseValues?.critical ?? markCompleteIncidentRecord?.isCritical)} />
            <ReopenIncidentModal
                visible={isReopenModalOpen}
                onClose={() => {
                    setIsReopenModalOpen(false);
                    setReopenIncidentRecord(null);
                }}
                statuses={reopenStatuses}
                onConfirm={handleReopen} />
            <AddDiaryEntryModal
                visible={isDiaryModalOpen}
                onClose={closeDiaryModal}
                onSubmit={handleDiaryEntrySubmit}
                isCritical={diaryIncident?.isCritical ?? false}
                initialEntry={editingDiaryEntry}
                onPreview={handleDiaryEntryPreview} />
        </Main>
    );
};

export default Incidents;
